import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend,  Label } from 'recharts';
import moment from "moment-timezone";
import { useStoreContext } from 'src/store/context';
import { getSamples, getHistoricFieldSampleDailies } from 'src/apis/getFuelSampleCalls';
import { useAddAlert } from 'src/store/actions';
import { ONEYR, errorResponse, findMaxByKey, findMinByKey, mergeArrays } from 'src/store/common';
import { alertMessages } from 'src/constants';

type FSChartProps = {
    id: any,
    siteMeta:any,
}
const CustomizedAxisTick: FunctionComponent<any> = (props: any) => {
  const { x, y, payload , index} = props;
  const shouldDisplayLabel = index % 4 ===0;
  return (
    <g transform={`translate(${x},${y})`} >
      {
        shouldDisplayLabel? (
          <text
            fontSize=".75rem"
            x={0}
            y={2}
            dy={16}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)"
          >
            {moment(payload.value).format('MMM')}
          </text>
        ):null
      }
    </g>
  );
};

const CustomTooltip:any = (props:any) => {
  const {active, label,payload, tzOffset}=props;
  if (active && payload && payload.length) {
    const currentValueExists = payload.find((p:any)=>p.name.includes('Current'))
    return (
      <div className="custom-tooltip" style={{backgroundColor: 'white', border: 'none !important', display:'block', margin:'3px', padding: '3px', height:'5rem', }}>
        <span className="month" style={{fontSize: '.75rem', paddingBottom: '0px', paddingTop:'0px', display:'block'}}>
        {currentValueExists? moment(label).utcOffset(tzOffset.offset).format("MMM DD, YYYY"): moment(label).utcOffset(tzOffset.offset).format("MMM DD")}
        </span>
        {
          payload.map((p:any)=>{
            return(
              <svg width="70" height="12" style={{display: 'block', marginTop: '2px', paddingBottom: '1px'}}>
                <rect width="7" height="7" style={{fill:p?.color}}/>
                  <text
                    fontSize=".75rem"
                    height="8"
                    x={10}
                    y={10}
                  >
                  {p ?`${p.value}`:''}
                </text>
              </svg>
            )
          })
        }
      </div>
    );
  }
  return null;
};

export const FSChartPopUp: React.FC<FSChartProps> = ({id, siteMeta}) => {
  const [ state, dispatch ] = useStoreContext();
  const [xTicks, setXTicks] = useState<number[]>([]);
  const [ allVars, setAllVars ] = useState<any[]>([]);
  const addAlert = useAddAlert(dispatch);
  const [, setSamplesCallComplete] = useState<boolean>(true);
  const [ yAllValues, setYAllValues] = useState<any[]>([]);
  const [yearToUse, setYearToUse] = useState<number>(moment().year())

  	/*fieldSampleResponse will format the data to date as a number*/
	const fieldSampleResponse = useCallback((response:any, historicDailies: any[])=>{
    let currentValues = response?.map((item: any) => {
			return({
        dateString:moment(item.sample).utc().format('MM-DD-YY'),
        sampleMonth: moment(item.sample).utc().format('MM-DD'),
				date: Number(moment(item.sample).utc().format('x')),
				sav: item.sample_average_value,
			})
		});
    let yearOfSampleStart = moment().year();
    if(response?.length>0){
      yearOfSampleStart = moment(response[0].sample).year();
      setYearToUse(yearOfSampleStart);
    }
    //adding years to the averages so that it can be plotted correctly
    let histAvgWYears:any[] =[];
    let startOfChartDate2 = moment().utc().year(yearOfSampleStart).startOf('year');

    while(startOfChartDate2<=moment().utc().year(yearOfSampleStart).endOf('year')){
      let monthDayCurrentIteration = moment(startOfChartDate2).utc().add(1, 'months').format('MM-DD');
      let currentMD = historicDailies.find(hist => hist.day === monthDayCurrentIteration)
      if(currentMD){
        let dateString = `${currentMD.day}-${yearOfSampleStart}`;
        histAvgWYears.push({
          dateString: moment(dateString).utc().format('MM-DD-YY'),
          date: Number(moment(dateString).utc().format('x')),
          sav: null,
          sampleMonth: currentMD.day,
          histMax: currentMD.sampleAverageValue.max,
          histMin: currentMD.sampleAverageValue.min,
          histAvg: currentMD.sampleAverageValue.avg,
        })
      }
      startOfChartDate2 = startOfChartDate2.add(1,'days');
    }
    let mergedHistCurrent = mergeArrays(histAvgWYears, currentValues, 'dateString')
    setYAllValues(mergedHistCurrent)
		setAllVars(mergedHistCurrent.toSorted(({date:a}, {date:b})=>a-b));
			  
	},[]);

	/*call to get field sample chart popuplated*/
	const fieldSampleCallback = useCallback(()=>{
		(async()=>{
			try{
				const requestVariableCurrentLine ={
					startDate: moment().year(moment(state.fsMapEndDate).year()).startOf('year'),
					endDate: moment().year(moment(state.fsMapEndDate).year()).endOf('year'),
					fuel_type: state.fsMapFuel? state.fsMapFuel.fuel_type: null,
					status: "Submitted",
					category: state.fsMapCategory,
					sub_category: state.fsMapSubCat?.code_key==='None'? null: state.fsMapSubCat.code_key,
					method: state.fsMapMethod?.code_key==='None'? null: state.fsMapMethod.code_key,
					siteId: id,
					sortBy: 'date_time',
					sortOrder: 'desc',
					page: 0,
					rowsPerPage: 300000,
				}
        const requestHistoric ={
          dateTimeFormat: null,
          endMonthDay: null,
          endYear: moment().set({ month: 0, date: 2 }).utc().format('YYYY').toString(),
          startMonthDay: null,
          startYear: '2000',
          siteId: Number(id),
          category: state.fsMapCategory,
          method: state.fsMapMethod?.code_key==='None'? null: state.fsMapMethod.code_key,
          sampleType: state.fsMapFuel? state.fsMapFuel.fuel_type: null,
          subCategory: state.fsMapSubCat?.code_key==='None'? null: state.fsMapSubCat.code_key,
          status: 'Submitted',
        }
				const [samples, historicDailies]:any = await Promise.all([
          getSamples(addAlert, setSamplesCallComplete, requestVariableCurrentLine), getHistoricFieldSampleDailies(requestHistoric)
        ]);

				if(historicDailies){
					fieldSampleResponse(samples?.samplesForHeader, historicDailies);
          let ticks: number[] = [];
          if (ticks.length < 1 && samples?.samplesForHeader?.length>0) {
            const yearOfSample = moment(samples?.samplesForHeader[0].sample).year()
            let i = moment().startOf('year').year(yearOfSample).format('MMM DD YYYY');
            ticks.push(Number(moment(i).format('x')));
            let j = 0;
            while (moment(i) <= moment().year(yearOfSample).endOf('year')) {
              j = j + 1;
              ticks.push(Number(moment(i).year(yearOfSample).add(1, 'month').format('x')))
              i = moment(moment(i).add(1, 'month')).format('MMM DD YYYY')
            }
            ticks.pop()
            ticks.pop()
            ticks.push(Number(moment().endOf('year').year(yearOfSample).format('x')))

            setXTicks(ticks)
          }
          else if(ticks.length < 1 && samples?.samplesForHeader?.length<=0) {
            let i = moment().startOf('year').format('MMM DD YYYY');
            ticks.push(Number(moment(i).format('x')));
            let j = 0;
            while (moment(i) <= moment().endOf('year')) {
              j = j + 1;
              ticks.push(Number(moment(i).add(1, 'month').format('x')))
              i = moment(moment(i).add(1, 'month')).format('MMM DD YYYY')
            }
            ticks.pop()
            ticks.pop()
            ticks.push(Number(moment().endOf('year').format('x')))

            setXTicks(ticks)
          }
				}
				
			}
			catch(error){
				errorResponse(error, addAlert, alertMessages.retrievingStationData)
			}
		})();
	},[addAlert, id, state.fsMapCategory, state.fsMapEndDate, state.fsMapFuel, state.fsMapMethod.code_key,
	 state.fsMapSubCat.code_key, fieldSampleResponse, ]);

  useMemo(()=>{
    if(state.mapDataType==='FieldSample' && siteMeta){
        fieldSampleCallback();
    }
  },[fieldSampleCallback, state.mapDataType, siteMeta]); 

  return(
    <div>
    {allVars.length>1?(
        <LineChart
          width={500}
          data={allVars}
          height={300}
          margin={{
            top: (5),
            right: 10,
            left: 10,
            bottom: 10,
          }}
        >
          
          <XAxis dataKey="date" scale="time" interval={0} type="number" tick={<CustomizedAxisTick />} domain={[Number(moment().year(yearToUse).startOf('year').format('x')), Number(moment().endOf('year').year(yearToUse).format('x'))]} ticks={xTicks}/>
          <YAxis yAxisId="left" orientation="left" domain={[ findMinByKey(yAllValues, 'histMin'),findMaxByKey(yAllValues, 'histMax')]} style={{fontSize: '0.7rem',fontFamily: 'serif'}}  axisLine={false}>
          <Label
            value={'Sample Avg Value (%)'}
            position="insideLeft"
            angle={-90}
            fontSize={'0.7rem'}
            style={{ textAnchor: 'middle' }}
          />
          </YAxis>
          <Tooltip content={<CustomTooltip tzOffset={state.userTimeZone}/>} position={{ y: -5 }} 
          />
          <Legend wrapperStyle={{fontSize:'11px', top:'290px'}} iconType="plainline" iconSize={10}/>
          <Line connectNulls={true} name={`Current SAV (${moment(Number(moment(state.fsMapEndDate).utc().utcOffset(siteMeta?.timeZoneOffset).format('x')) - ONEYR).set('date', 1).format('MMM YYYY')}-${moment(Number(moment(state.fsMapEndDate).utc().utcOffset(siteMeta?.timeZoneOffset).format('x'))).format('MMM YYYY')})`} type="monotone" yAxisId ="left" dataKey="sav" stroke="black" dot={true} strokeWidth={2}/>
          <Line connectNulls={true} name={'Historical Average'} type="monotone" yAxisId ="left" dataKey="histAvg" stroke="green" dot={false} strokeWidth={1}/>
          <Line connectNulls={true} name={'Historical Max'} type="monotone" yAxisId ="left" dataKey="histMax" stroke="blue" dot={false} strokeWidth={1}/>
          <Line connectNulls={true} name={'Historical Min'} type="monotone" yAxisId ="left" dataKey="histMin" stroke="red" dot={false} strokeWidth={1}/>

        </LineChart>
    ):(null)}
    </div>

  );
}